/* eslint-disable no-nested-ternary */
import {
  Button,
  Heading,
  Pagination,
  TabBar,
  TextField,
} from '@tmov/design-system';
import { Search, Sliders, WaveVariantB } from '@tmov/design-system/icons';
import { notification } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { LoadingContainer } from '../../../components/LoadingContainer';
import NoDataContainer from '../../../components/NoDataContainer';
import geradorIdUnico from '../../../helpers/geradores';
import { gTagEntrada } from '../../../helpers/googleAnalytics';
import { useAuth } from '../../../security/authContext';
import { checkPermission } from '../../../security/Can';
import apiNetCore from '../../../services/apiNetCore';
import StatusDemandaEnum from '../Enum/StatusDemandaEnum';
import DemandaTable from './DemandaTable';
import FiltersDrawer from './FiltersDrawer';
import {
  Container,
  Header,
  PaginationContainer,
  SearchControls,
  SearchControlsLeft,
  SearchControlsRight,
} from './styles';

const initialFilters = {
  idProduto: null,
  idCidadeOrigem: null,
  idCidadeDestino: null,
  idEstadoOrigem: null,
  idEstadoDestino: null,
};

export default function Listagem() {
  const { user } = useAuth();

  const history = useHistory();
  const [drawerActive, setDrawerActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demandas, setDemandas] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(1);

  const [idStatus, setIdStatus] = useState(1);

  const [filters, setFilters] = useState(initialFilters);

  const [idDemandaSearch, setIdDemandaSearch] = useState('');
  const [idDemanda, setIdDemanda] = useState();
  const [idDemandaError, setIdDemandaError] = useState(false);

  const [statusCounts, setStatusCounts] = useState({
    [StatusDemandaEnum.CARREGANDO]: 0,
    [StatusDemandaEnum.SUSPENSO]: 0,
    [StatusDemandaEnum.FINALIZADO]: 0,
  });

  const currentDemandas = useMemo(
    () =>
      loading ? (
        <LoadingContainer>
          <WaveVariantB size="60px" />
        </LoadingContainer>
      ) : demandas?.length > 0 ? (
        demandas.map(demanda => (
          <DemandaTable key={demanda.id} demanda={demanda} />
        ))
      ) : (
        <NoDataContainer />
      ),
    [demandas, loading]
  );

  const tabsDemandas = useMemo(
    () =>
      [
        StatusDemandaEnum.CARREGANDO,
        StatusDemandaEnum.SUSPENSO,
        StatusDemandaEnum.FINALIZADO,
      ].map(status => ({
        id: status,
        title: StatusDemandaEnum.getTipo(status).label,
        ...(statusCounts[status] >= 0 && {
          count: statusCounts[status],
        }),
        content: currentDemandas,
      })),
    [currentDemandas, statusCounts]
  );

  const handleChangeIdDemanda = useDebouncedCallback(search => {
    setIdDemanda(search);
  }, 350);

  const handleChangeLoteSearchKeyPress = useCallback(e => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
      setIdDemandaError(true);
      return;
    }
    setIdDemandaError(false);
  }, []);

  const handleChangeLoteSearch = useCallback(
    e => {
      const search = e.target.value;
      setIdDemandaSearch(search);
      handleChangeIdDemanda(search);
    },
    [handleChangeIdDemanda]
  );

  const handleChangeTab = tab => {
    setIdStatus(tab);
    setCurrentPage(1);
  };

  const handlerOnChangePagination = useCallback(async () => {
    const {
      produto,
      cidadeOrigem,
      cidadeDestino,
      estadoOrigem,
      estadoDestino,
    } = filters;

    try {
      setLoading(true);

      const body = {
        page: currentPage,
        size: 10,
        idCliente: user.clientes_pagadores,
        ...(idDemanda && { id: idDemanda }),
        ...(idStatus && { idStatus }),
        ...(produto && { idProduto: produto.id }),
        ...(cidadeOrigem && { idCidadeOrigem: cidadeOrigem.id }),
        ...(cidadeDestino && { idCidadeDestino: cidadeDestino.id }),
        ...(estadoOrigem && { idEstadoOrigem: estadoOrigem.id }),
        ...(estadoDestino && { idEstadoDestino: estadoDestino.id }),
      };

      const { data } = await apiNetCore.post(
        '/demanda/obter-demandas-paginadas',
        body
      );
      setDemandas(data.content);
      setNumPages(data.totalPages);
      setStatusCounts({
        [StatusDemandaEnum.CARREGANDO]: data.totalCarregando,
        [StatusDemandaEnum.SUSPENSO]: data.totalSuspenso,
        [StatusDemandaEnum.FINALIZADO]: data.totalFinalizado,
      });
    } catch (e) {
      notification.error({
        message: 'Ops, não foi possível recuperar as demandas',
        description:
          'Por favor, entre em contato com nosso suporte caso o problema persistir.',
      });
      setDemandas([]);
      setNumPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, idDemanda, idStatus, user.clientes_pagadores]);

  const handleGoToNewDemandaPage = () => {
    const eventId = geradorIdUnico();
    history.push({ pathname: '/lotes/novo', state: { eventId } });
    gTagEntrada(
      'Click - Nova Demanda',
      'Demanda',
      'Criação de nova demanda.',
      eventId
    );
  };

  const hasCriarDemandaPermission = checkPermission(
    user.permissions,
    'lotes:create'
  );
  useEffect(() => {
    handlerOnChangePagination();
  }, [handlerOnChangePagination]);

  return (
    <>
      <Container>
        <Header>
          <Heading variant="h2">Demandas</Heading>
          {hasCriarDemandaPermission && (
            <Button onClick={handleGoToNewDemandaPage}>Nova demanda</Button>
          )}
        </Header>
        <SearchControls>
          <SearchControlsLeft>
            <Button
              variant="outlined"
              onClick={() => setDrawerActive(prev => !prev)}
            >
              <Sliders /> Filtrar
            </Button>
            <TextField
              value={idDemandaSearch}
              onKeyPress={handleChangeLoteSearchKeyPress}
              onChange={handleChangeLoteSearch}
              label="Pesquisar lote por ID"
              trailingIcon={Search}
              error={idDemandaError}
              supportingText={
                idDemandaError ? 'Este campo apenas aceita números' : ''
              }
            />
          </SearchControlsLeft>
          <SearchControlsRight />
        </SearchControls>
        <TabBar
          selectedTabId={idStatus}
          tabs={tabsDemandas}
          onChange={handleChangeTab}
        />
      </Container>
      <PaginationContainer>
        <Pagination
          currentPage={currentPage}
          onChange={page => setCurrentPage(page)}
          numPages={numPages}
          disabled={loading}
        />
      </PaginationContainer>
      <FiltersDrawer
        drawerActive={drawerActive}
        setDrawerActive={setDrawerActive}
        filters={filters}
        updateFilters={setFilters}
      />
    </>
  );
}
